import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClient } from '../hooks/useClients';
import { useClientWeights, useClientBodyMeasurements, useClientWorkoutsStats, useClientExercisesStats, useClientExercisesWithWeight, useClientExerciseProgress, useClientBestProgressExercise } from '../hooks/useClients';
import WeightChart from '../components/WeightChart';
import MeasurementsChart from '../components/MeasurementsChart';
import { getTrackedMeasurements } from '../utils/bodyMeasurements';
import WorkoutsChart from '../components/WorkoutsChart';
import ExercisesPieChart from '../components/ExercisesPieChart';
import ExerciseSelector from '../components/ExerciseSelector';
import ExerciseProgressChart from '../components/ExerciseProgressChart';
import Navigation from '../components/Navigation';
import { ArrowLeft, TrendingUp, BarChart3 } from 'lucide-react';
import { ClientExercise } from '../types';

const ClientStatsPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { trainer } = useAuth();
  
  const clientIdNumber = parseInt(clientId || '0');
  
  const { client, isLoading: isClientLoading } = useClient(clientIdNumber);
  const { weights, isLoading: isWeightsLoading } = useClientWeights(clientIdNumber);
  const { measurements, isLoading: isMeasurementsLoading } = useClientBodyMeasurements(clientIdNumber);
  const { workoutsStats, isLoading: isWorkoutsLoading } = useClientWorkoutsStats(clientIdNumber);
  const { exercisesStats, isLoading: isExercisesLoading } = useClientExercisesStats(clientIdNumber);
  const { exercises, isLoading: isExercisesWithWeightLoading } = useClientExercisesWithWeight(clientIdNumber);
  const { bestExercise, isLoading: isBestExerciseLoading } = useClientBestProgressExercise(clientIdNumber);
  
  const [selectedExercise, setSelectedExercise] = useState<ClientExercise | null>(null);
  
  // Получаем прогресс выбранного упражнения или лучшего упражнения по умолчанию
  const exerciseId = selectedExercise?.id || bestExercise?.id;
  const { progress, isLoading: isProgressLoading } = useClientExerciseProgress(clientIdNumber, exerciseId || 0);
  
  console.log('ClientStatsPage - exercises:', exercises);
  console.log('ClientStatsPage - bestExercise:', bestExercise);
  console.log('ClientStatsPage - selectedExercise:', selectedExercise);
  console.log('ClientStatsPage - exerciseId:', exerciseId);
  console.log('ClientStatsPage - progress:', progress);
  
  // Устанавливаем лучшее упражнение по умолчанию при загрузке
  React.useEffect(() => {
    if (bestExercise && !selectedExercise) {
      console.log('ClientStatsPage - setting best exercise as default:', bestExercise);
      setSelectedExercise({
        id: bestExercise.id,
        name: bestExercise.name,
        description: ''
      });
    }
  }, [bestExercise, selectedExercise]);


  if (isClientLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Клиент не найден</h1>
            <button
              onClick={() => navigate('/clients')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Вернуться к клиентам
            </button>
          </div>
        </div>
      </div>
    );
  }

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };



  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Заголовок страницы */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/clients')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Назад
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Статистика клиента {client.first_name} {client.last_name}
                </h1>
              </div>
            </div>
          </div>
        </div>



        {/* График тренировок и распределение упражнений в одной строке */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* График тренировок */}
          <div>
            {isWorkoutsLoading ? (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : workoutsStats.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <TrendingUp className="mx-auto h-8 w-8 text-gray-400 mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Нет данных о тренировках
                </h3>
                <p className="text-xs text-gray-500">
                  У клиента пока нет записей о тренировках.
                </p>
              </div>
            ) : (
              <WorkoutsChart data={workoutsStats} />
            )}
          </div>

          {/* Круговая диаграмма упражнений */}
          <div>
            {isExercisesLoading ? (
              <div className="bg-white rounded-lg shadow p-4">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : exercisesStats.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <BarChart3 className="mx-auto h-8 w-8 text-gray-400 mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Нет данных об упражнениях
                </h3>
                <p className="text-xs text-gray-500">
                  У клиента пока нет записей об упражнениях.
                </p>
              </div>
            ) : (
              <ExercisesPieChart data={exercisesStats} />
            )}
          </div>
        </div>

        {/* Нижние графики */}
        <div className="space-y-8">
          {/* График веса */}
          <div>
            {isWeightsLoading ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : weights.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <TrendingUp className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Нет данных о весе
                </h3>
                <p className="text-gray-500 mb-6">
                  У клиента пока нет записей о весе. Добавьте первую запись веса, чтобы увидеть график прогресса.
                </p>
                <button
                  onClick={() => navigate('/clients')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Добавить вес
                </button>
              </div>
            ) : (
              <WeightChart data={weights} />
            )}
          </div>

          {/* График замеров */}
          <div>
            {isMeasurementsLoading ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : measurements.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Нет данных о замерах
                </h3>
                <p className="text-gray-500 mb-6">
                  У клиента пока нет записей о замерах тела. Добавьте первые замеры, чтобы увидеть график изменений.
                </p>
                <button
                  onClick={() => navigate('/clients')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Добавить замеры
                </button>
              </div>
            ) : (
              <MeasurementsChart data={measurements} tracked={getTrackedMeasurements(client)} />
            )}
          </div>

          {/* Статистика по упражнению */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Статистика по упражнению</h3>
            <ExerciseSelector
              exercises={exercises}
              selectedExercise={selectedExercise}
              onExerciseSelect={setSelectedExercise}
              isLoading={isExercisesWithWeightLoading}
            />
            
            {selectedExercise && (
              <div className="mt-6">
                <ExerciseProgressChart
                  data={progress}
                  exerciseName={selectedExercise.name}
                />
              </div>
            )}
          </div>
        </div>

        {/* Дополнительная статистика */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Записей веса</p>
                <p className="text-2xl font-semibold text-gray-900">{weights.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Записей замеров</p>
                <p className="text-2xl font-semibold text-gray-900">{measurements.length}</p>
              </div>
            </div>
          </div>

          {weights.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Текущий вес</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {weights[weights.length - 1]?.weight.toFixed(1)} кг
                  </p>
                </div>
              </div>
            </div>
          )}

          {weights.length > 1 && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Изменение веса</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {weights.length > 1 ? 
                      (weights[weights.length - 1].weight - weights[0].weight).toFixed(1) : 
                      '0.0'} кг
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientStatsPage; 